import { FilterColumns } from "@mijadesign/web";
import type { FilterColumnsProps, FormInstance } from "@mijadesign/web";
import type { ReactNode } from "react";
import { useCallback, useMemo, useRef } from "react";
import { useEventEmitter, type EventEmitter } from "../../event-runtime";

interface FilterColumnsWrapperProps extends FilterColumnsProps<
  Record<string, unknown>
> {
  onEvent?: EventEmitter;
  renderSlot?: (
    slotName: string,
    payload?: Record<string, unknown>,
  ) => ReactNode;
}

function parsePattern(pattern: unknown): unknown {
  if (pattern instanceof RegExp || typeof pattern !== "string") {
    return pattern;
  }
  const trimmedPattern = pattern.trim();
  if (!trimmedPattern) {
    return undefined;
  }
  try {
    // 兼容 "/xxx/i" 和 "xxx" 两种输入形式。
    const slashPatternMatch = trimmedPattern.match(/^\/(.+)\/([gimsuy]*)$/);
    if (slashPatternMatch) {
      return new RegExp(slashPatternMatch[1], slashPatternMatch[2]);
    }
    return new RegExp(trimmedPattern);
  } catch {
    return pattern;
  }
}

function FilterColumnsWrapper({
  onSubmit,
  onReset,
  onCollapsedChange,
  onEvent,
  ...rest
}: FilterColumnsWrapperProps) {
  const formRef = useRef<FormInstance | undefined>(undefined);
  const emitEvent = useEventEmitter(onEvent);
  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(rest.options)) {
      return rest.options;
    }
    return rest.options.map((option) => {
      if (!option || typeof option !== "object" || Array.isArray(option)) {
        return option;
      }
      const formItemProps = (option as Record<string, unknown>).formItemProps;
      if (
        !formItemProps ||
        typeof formItemProps !== "object" ||
        Array.isArray(formItemProps)
      ) {
        return option;
      }
      const rules = (formItemProps as Record<string, unknown>).rules;
      if (!Array.isArray(rules)) {
        return option;
      }
      let hasPatternChanged = false;
      const normalizedRules = rules.map((rule) => {
        if (!rule || typeof rule !== "object" || Array.isArray(rule)) {
          return rule;
        }
        const currentPattern = (rule as Record<string, unknown>).pattern;
        const nextPattern = parsePattern(currentPattern);
        if (nextPattern === currentPattern) {
          return rule;
        }
        hasPatternChanged = true;
        return {
          ...rule,
          pattern: nextPattern,
        };
      });
      if (!hasPatternChanged) {
        return option;
      }
      return {
        ...option,
        formItemProps: {
          ...formItemProps,
          rules: normalizedRules,
        },
      };
    });
  }, [rest.options]);

  const handleSubmit = useCallback(
    async (value: Record<string, unknown>, isReset?: boolean) => {
      await formRef.current?.validateFields();
      emitEvent("filterColumns.submit", {
        value,
        isReset: Boolean(isReset),
      });
      onSubmit?.(value, isReset);
    },
    [emitEvent, onSubmit],
  );

  const handleReset = useCallback(() => {
    emitEvent("filterColumns.reset", {});
    onReset?.();
  }, [emitEvent, onReset]);

  const handleCollapsedChange = useCallback(
    (collapsed: boolean) => {
      emitEvent("filterColumns.collapsed.change", { collapsed });
      onCollapsedChange?.(collapsed);
    },
    [emitEvent, onCollapsedChange],
  );

  return (
    <FilterColumns
      {...rest}
      options={normalizedOptions}
      formRef={formRef}
      onSubmit={handleSubmit}
      onReset={handleReset}
      onCollapsedChange={handleCollapsedChange}
    />
  );
}

export default FilterColumnsWrapper;
